import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Download, Send, CheckCircle, XCircle } from 'lucide-react';
import EstimateTemplate from './EstimateTemplate';

const EstimatePreview = ({ estimate, lineItems, customer, company, onClose, onApprove, onReject, onDownloadPdf, onSendEmail }) => {
  const [isLoading, setIsLoading] = useState(false);
  const estimateRef = useRef();

  const handleApprove = async () => {
    setIsLoading(true);
    try {
      await onApprove();
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    setIsLoading(true);
    try {
      await onReject();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEmailClick = async () => {
    setIsLoading(true);
    try {
      await onSendEmail();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdfClick = async () => {
    setIsLoading(true);
    try {
      await onDownloadPdf();
    } finally {
      setIsLoading(false);
    }
  };

  const getApprovalBadgeColor = (status) => {
    switch(status) {
      case 'pending_approval': return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'approved': return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'rejected': return 'bg-rose-50 text-rose-700 border border-rose-200';
      default: return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-slate-100 text-slate-700 border border-slate-200';
      case 'sent':
        return 'bg-sky-50 text-sky-700 border border-sky-200';
      case 'accepted':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'rejected':
        return 'bg-rose-50 text-rose-700 border border-rose-200';
      case 'expired':
        return 'bg-orange-50 text-orange-700 border border-orange-200';
      default:
        return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-x-0 bottom-0 top-20 z-[70] overflow-y-auto bg-slate-900/72 p-2 backdrop-blur-sm sm:top-24 sm:p-4 lg:left-80 lg:top-24 lg:p-6">
      <div className="mx-auto flex min-h-full w-full max-w-[96vw] flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl lg:max-w-[calc(100vw-22rem)]">

        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-sky-900 px-6 py-6 text-white sm:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.24em] text-slate-200">Project Budget Estimate</p>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-bold leading-tight sm:text-3xl">Estimate #{estimate.estimateNumber}</h2>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeColor(estimate.status)}`}>
                  {(estimate.status || 'draft').toUpperCase()}
                </span>
              </div>
              <p className="text-sm text-slate-200">Review scope, approve pricing, and generate polished client-ready documents.</p>
            </div>

            <div className="flex items-center gap-3">
              <span className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold tracking-wide ${getApprovalBadgeColor(estimate.approval_status)}`}>
                APPROVAL: {(estimate.approval_status || 'pending_approval').replace(/_/g, ' ').toUpperCase()}
              </span>
              <button
                onClick={onClose}
                className="rounded-xl border border-white/20 bg-white/10 p-2.5 transition hover:bg-white/20"
                title="Close"
              >
                <X size={22} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 py-4 sm:px-8">
          <div className="flex flex-wrap gap-2.5">
            {estimate.approval_status === 'pending_approval' && (
              <>
                <button
                  onClick={handleReject}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
                  title="Reject this estimate"
                >
                  <XCircle size={18} />
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
                  title="Approve this estimate"
                >
                  <CheckCircle size={18} />
                  Approve
                </button>
              </>
            )}

            {estimate.approval_status === 'approved' && estimate.status === 'draft' && (
              <>
                <button
                  onClick={handleDownloadPdfClick}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:opacity-50"
                  title="Download as PDF"
                >
                  <Download size={18} />
                  Download PDF
                </button>
                <button
                  onClick={handleSendEmailClick}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-700 disabled:opacity-50"
                  title="Send via email"
                >
                  <Send size={18} />
                  Send Email
                </button>
              </>
            )}

            {estimate.status === 'sent' && (
              <button
                onClick={handleDownloadPdfClick}
                disabled={isLoading}
                className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:opacity-50"
                title="Download as PDF"
              >
                <Download size={18} />
                Download PDF
              </button>
            )}
          </div>

          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-slate-100 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
            title="Close preview"
          >
            <X size={18} />
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 via-slate-100 to-sky-50 px-2 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-6">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <EstimateTemplate 
              ref={estimateRef}
              estimate={estimate} 
              lineItems={lineItems}
              customer={customer || {}} 
              company={company || {}} 
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EstimatePreview;
